import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { WorkflowContainer, User, UserRole } from "../types";
import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";

const SYSTEM_USERNAMES = new Set(["admin", "employee"]);
const SYSTEM_EMAILS = new Set(["admin@workflowms.com", "employee@workflowms.com"]);
const SYSTEM_FULL_NAMES = new Set(["system administrator", "test employee"]);

const isSystemUser = (candidate: User) => {
  const username = (candidate.username || "").toLowerCase();
  const email = (candidate.email || "").toLowerCase();
  const firstName = (candidate.firstName || "").toLowerCase();
  const lastName = (candidate.lastName || "").toLowerCase();
  const fullName = `${firstName} ${lastName}`.trim();
  return (
    SYSTEM_USERNAMES.has(username) ||
    SYSTEM_EMAILS.has(email) ||
    SYSTEM_FULL_NAMES.has(fullName)
  );
};

export const AddProjectMember: React.FC = () => {
  const { t } = useTranslation(["projects", "users", "common", "errors"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    user,
    isManager,
    isAdmin,
    isProjectManager,
    getWorkflowLabel,
    hasWorkflowTerminologyOverride,
  } = useAuth();

  const [project, setProject] = useState<WorkflowContainer | null>(null);
  const [projectMembers, setProjectMembers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const defaultRole = "team_member";
  const [canStartWorkflow, setCanStartWorkflow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addingAll, setAddingAll] = useState(false);

  const workflowText = getWorkflowLabel();
  const useWorkflowOverride = hasWorkflowTerminologyOverride();
  const projectMembersTitle = useWorkflowOverride
    ? `${workflowText} Üyeleri`
    : t("projects:projectMembers");
  const canRemoveMembers = isManager() || isAdmin() || isProjectManager();
  const currentUserId = user?.id;

  // Edit member states
  const [showEditMember, setShowEditMember] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [editRole, setEditRole] = useState<string>("");
  const [editCanStartWorkflow, setEditCanStartWorkflow] =
    useState<boolean>(false);

  useEffect(() => {
    if (id) {
      loadProjectData();
      loadProjectMembers();
      loadCompanyUsers();
    }
  }, [id]);

  const loadCompanyUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await apiService.getUsers();
      setAllUsers(users);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || t("users:loadUsersFailed");
      toast.error(errorMessage);
      setAllUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const eligibleUsers = useMemo(() => {
    const currentUserId = user?.id;
    return allUsers.filter((candidate) => {
      if (currentUserId && candidate.id === currentUserId) {
        return false;
      }
      if (isSystemUser(candidate)) {
        return false;
      }
      return true;
    });
  }, [allUsers, user]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return eligibleUsers;
    }

    return eligibleUsers.filter((candidate) => {
      const firstName = (candidate.firstName || "").toLowerCase();
      const lastName = (candidate.lastName || "").toLowerCase();
      const email = (candidate.email || "").toLowerCase();
      return (
        firstName.includes(normalizedSearch) ||
        lastName.includes(normalizedSearch) ||
        email.includes(normalizedSearch)
      );
    });
  }, [eligibleUsers, searchTerm]);

  const memberUserIds = useMemo(
    () => new Set(projectMembers.map((member) => member.id)),
    [projectMembers]
  );

  const projectManagers = useMemo(
    () =>
      projectMembers.filter(
        (member: any) =>
          member.projectRole === "project_manager" &&
          member.role === UserRole.PROJECT_MANAGER
      ),
    [projectMembers]
  );

  const managers = useMemo(
    () => projectMembers.filter((member: any) => member.role === UserRole.MANAGER),
    [projectMembers]
  );

  const teamMembers = useMemo(
    () =>
      projectMembers.filter((member: any) => {
        const isProjectManager =
          member.projectRole === "project_manager" &&
          member.role === UserRole.PROJECT_MANAGER;
        const isManager = member.role === UserRole.MANAGER;
        return !isProjectManager && !isManager;
      }),
    [projectMembers]
  );

  const addableUsers = useMemo(
    () => eligibleUsers.filter((candidate) => !memberUserIds.has(candidate.id)),
    [eligibleUsers, memberUserIds]
  );
  const isMutatingMembers = adding || addingAll;

  const loadProjectData = async () => {
    try {
      const projectData = await apiService.getWorkflowContainerById(
        parseInt(id!)
      );
      setProject(projectData);
    } catch (error) {
      toast.error(t("errors:loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadProjectMembers = async () => {
    try {
      const members = await apiService.getWorkflowContainerMembers(
        parseInt(id!)
      );
      // Backend'den gelen proje üyeleri hem User hem de role bilgisi içeriyor
      setProjectMembers(
        members.map((member: any) => ({
          ...member.user,
          projectRole: member.role, // Proje içindeki rolü ayrı olarak sakla
          memberId: member.id, // Üye çıkarma için member ID'si
          canStartWorkflow: member.canStartWorkflow, // Workflow başlatma yetkisi
        }))
      );
    } catch (error) {
      toast.error(t("errors:loadFailed"));
    }
  };

  const handleSelectUser = async (user: User) => {
    setAdding(true);
    try {
      await apiService.addWorkflowContainerMember(
        parseInt(id!),
        user.id,
        defaultRole,
        false
      );
      toast.success(
        t("projects:memberAdded", {
          name: `${user.firstName} ${user.lastName}`,
        })
      );

      // Proje üyelerini yenile
      await loadProjectMembers();

      // Arama sonuçlarını temizle
      setSearchTerm("");
    } catch (error) {
      toast.error(t("errors:createFailed"));
    } finally {
      setAdding(false);
    }
  };

  const handleAddAllEmployees = async () => {
    if (!id || addingAll) {
      return;
    }

    if (addableUsers.length === 0) {
      toast(t("projects:noAvailableEmployees"));
      return;
    }

    setAddingAll(true);
    let addedCount = 0;
    let failedCount = 0;

    try {
      for (const candidate of addableUsers) {
        try {
          await apiService.addWorkflowContainerMember(
            parseInt(id, 10),
            candidate.id,
            defaultRole,
            false
          );
          addedCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      await loadProjectMembers();

      if (addedCount > 0) {
        toast.success(
          t("projects:membersAdded", {
            count: addedCount,
          })
        );
      }
      if (failedCount > 0) {
        toast.error(
          t("projects:membersAddFailed", {
            count: failedCount,
          })
        );
      }
    } finally {
      setAddingAll(false);
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!window.confirm(t("projects:confirmRemoveMember"))) {
      return;
    }

    try {
      await apiService.removeWorkflowContainerMember(parseInt(id!), memberId);
      toast.success(t("projects:memberRemoved"));

      // Proje üyelerini yenile
      await loadProjectMembers();
    } catch (error) {
      toast.error(t("errors:deleteFailed"));
    }
  };

  const handleEditMember = (member: any) => {
    setEditingMember(member);
    setEditRole(member.projectRole);
    setEditCanStartWorkflow(member.canStartWorkflow || false);
    setShowEditMember(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;

    try {
      await apiService.updateWorkflowContainerMember(
        parseInt(id!),
        editingMember.memberId,
        {
          role: editRole,
          canStartWorkflow: editCanStartWorkflow,
        }
      );

      await loadProjectMembers(); // Refresh
      setShowEditMember(false);
      setEditingMember(null);
      toast.success(t("projects:memberUpdated"));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || t("errors:updateFailed");
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="app-page pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("common:loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page bg-gray-50 pt-16">
      <div className="max-w-6xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {projectMembersTitle}
                </h1>
                {project?.formElements?.find((el) => el.id === "name")
                  ?.value && (
                  <p className="text-gray-600 text-sm mt-1">
                    {
                      project.formElements?.find((el) => el.id === "name")
                        ?.value
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Member Modal */}
        {showEditMember && editingMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold flex items-center">
                  <PencilIcon className="w-5 h-5 mr-2 text-blue-600" />
                  {t("projects:editMemberInfo")}
                </h3>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Kullanıcı Bilgisi (Read-only) */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                      {editingMember.firstName[0]}
                      {editingMember.lastName[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 truncate">
                        {editingMember.firstName} {editingMember.lastName}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {editingMember.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Role Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("auth:role")}
                  </label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="team_member">
                      {t("projects:roles.teamMember")}
                    </option>
                    <option value="lead">{t("projects:roles.lead")}</option>
                    <option value="reviewer">
                      {t("projects:roles.reviewer")}
                    </option>
                  </select>
                </div>

                {/* Workflow Permission */}
                <div className="border-t pt-4">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editCanStartWorkflow}
                      onChange={(e) =>
                        setEditCanStartWorkflow(e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 mt-0.5 mr-3 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        {t("projects:canStartWorkflow")}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("projects:canStartWorkflowDescription")}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditMember(false);
                    setEditingMember(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("common:cancel")}
                </button>
                <button
                  onClick={handleUpdateMember}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t("common:save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Members */}
          <div className="bg-white rounded-lg shadow-sm border min-h-[640px] lg:min-h-[780px] flex flex-col">
            <div className="p-4 border-b">
              <div className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {t("projects:currentMembers", {
                    count: projectMembers.length,
                  })}
                </h2>
              </div>
            </div>
            <div className="p-4 flex-1">
              {projectMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <UserIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>{t("projects:noProjectMembers")}</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[520px] lg:max-h-[680px] overflow-y-auto pr-1">
                  {/* Managers Section */}
                  {managers.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <UserIcon className="w-4 h-4 mr-1.5 text-indigo-600" />
                        {t("projects:managers")} ({managers.length})
                      </h3>
                      <div className="space-y-3">
                        {managers.map((member) => (
                          <div
                            key={member.id}
                            className="flex flex-col p-3 bg-indigo-50 border border-indigo-200 rounded-lg"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                                  <UserIcon className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {member.firstName} {member.lastName}
                                  </p>
                                  <p className="text-xs text-gray-600 truncate">
                                    {member.email}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                <span className="px-2 py-1 text-[10px] font-medium text-indigo-700 bg-indigo-100 rounded-full">
                                  {t("common:roles.manager")}
                                </span>
                                {canRemoveMembers && member.id !== currentUserId && (
                                  <button
                                    onClick={() =>
                                      handleRemoveMember((member as any).memberId)
                                    }
                                    className="px-2 py-1 text-[11px] text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    {t("common:remove")}
                                  </button>
                                )}
                              </div>
                            </div>
                            {(member as any).canStartWorkflow && (
                              <div className="flex items-center text-[10px] text-green-700 bg-green-50 px-2 py-1 rounded mt-1">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                {t("projects:canStartWorkflowShort")}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project Managers Section */}
                  {projectManagers.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <svg
                          className="w-4 h-4 mr-1.5 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        {t("projects:projectManagers")} ({projectManagers.length})
                      </h3>
                      <div className="space-y-3">
                        {projectManagers.map((member) => (
                          <div
                            key={member.id}
                            className="flex flex-col p-3 bg-purple-50 border border-purple-200 rounded-lg"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                                  <UserIcon className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {member.firstName} {member.lastName}
                                  </p>
                                  <p className="text-xs text-gray-600 truncate">
                                    {member.email}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                <span className="px-2 py-1 text-[10px] font-medium text-purple-700 bg-purple-100 rounded-full">
                                  {t("projects:roles.projectManager")}
                                </span>
                                {canRemoveMembers && member.id !== currentUserId && (
                                  <button
                                    onClick={() =>
                                      handleRemoveMember((member as any).memberId)
                                    }
                                    className="px-2 py-1 text-[11px] text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    {t("common:remove")}
                                  </button>
                                )}
                              </div>
                            </div>
                            {(member as any).canStartWorkflow && (
                              <div className="flex items-center text-[10px] text-green-700 bg-green-50 px-2 py-1 rounded mt-1">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                {t("projects:canStartWorkflowShort")}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Team Members Section */}
                  {teamMembers.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <UserIcon className="w-4 h-4 mr-1.5 text-blue-600" />
                        {t("projects:teamMembers")} (
                        {teamMembers.length}
                        )
                      </h3>
                      <div className="space-y-3">
                        {teamMembers.map((member) => (
                            <div
                              key={member.id}
                              className="flex flex-col p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                    <UserIcon className="w-4 h-4" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {member.firstName} {member.lastName}
                                    </p>
                                    <p className="text-xs text-gray-600 truncate">
                                      {member.email}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                  {canRemoveMembers && (
                                    <button
                                      onClick={() => handleEditMember(member)}
                                      className="px-2 py-1 text-[11px] text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    >
                                      {t("common:edit")}
                                    </button>
                                  )}
                                  {/* Proje yöneticisi projeden çıkarılamaz */}
                              {canRemoveMembers &&
                                member.id !== currentUserId &&
                                (member as any).projectRole !==
                                      "project_manager" && (
                                <button
                                  onClick={() =>
                                    handleRemoveMember(
                                      (member as any).memberId
                                    )
                                      }
                                      className="px-2 py-1 text-[11px] text-red-600 hover:bg-red-50 rounded transition-colors"
                                    >
                                      {t("common:remove")}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 items-center">
                                <span className="px-2 py-1 text-[10px] bg-blue-100 text-blue-700 rounded">
                                  {(member as any).projectRole}
                                </span>
                                {(member as any).canStartWorkflow ? (
                                  <span className="px-2 py-1 text-[10px] bg-green-100 text-green-700 rounded flex items-center gap-1">
                                    🚀 {t("projects:authorized")}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-[10px] bg-gray-100 text-gray-500 rounded flex items-center gap-1">
                                    🔒 {t("projects:noPermission")}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Add New Member */}
          <div className="bg-white rounded-lg shadow-sm border min-h-[640px] lg:min-h-[780px] flex flex-col">
            <div className="p-4 border-b">
              <div className="flex items-center space-x-2">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {t("projects:addMember")}
                </h2>
              </div>
            </div>
            <div className="p-4 space-y-4 flex-1">
              {/* Search Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("users:searchUser")}
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("users:searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Users List */}
              <div>
                <div className="flex items-center justify-between mb-3 gap-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    {searchTerm.trim()
                      ? t("users:searchResults")
                      : t("users:title")}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAddAllEmployees}
                      disabled={loadingUsers || isMutatingMembers}
                      className="px-3 py-1.5 text-[11px] font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isMutatingMembers
                        ? t("common:loading")
                        : t("projects:addAllEmployees")}
                    </button>
                    {loadingUsers && (
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        <span>{t("users:loadingUsers")}</span>
                      </div>
                    )}
                  </div>
                </div>

                {loadingUsers ? (
                  <div className="text-center py-6 text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="text-sm">{t("users:loadingUsers")}</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="text-sm">
                      {searchTerm.trim()
                        ? t("users:userNotFound")
                        : t("users:noUsers")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[520px] lg:max-h-[680px] overflow-y-auto pr-1">
                    {filteredUsers.map((user) => {
                      const isProjectMember = memberUserIds.has(user.id);

                      return (
                        <div
                          key={user.id}
                          className={`p-3 border rounded-lg ${
                            isProjectMember
                              ? "border-gray-200 bg-gray-50 opacity-60"
                              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer"
                          }`}
                          onClick={() =>
                            !isProjectMember &&
                            !isMutatingMembers &&
                            handleSelectUser(user)
                          }
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isProjectMember
                                    ? "bg-gray-300"
                                    : "bg-blue-100 text-blue-600"
                                }`}
                              >
                                <UserIcon className="w-4 h-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-xs text-gray-600 truncate">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                            {isProjectMember ? (
                              <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">
                                {t("projects:alreadyMember")}
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectUser(user);
                                }}
                                disabled={isMutatingMembers}
                                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[11px] rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-2"
                              >
                                {isMutatingMembers
                                  ? t("common:loading")
                                  : t("common:add")}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

